import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FirebaseX } from '@awesome-cordova-plugins/firebase-x/ngx';
import { AlertController, ModalController, NavController, Platform } from '@ionic/angular';
import { Subscription, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, take } from 'rxjs/operators';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { AccountService } from 'src/app/shared/services/account.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { NotificationAgreementRequest } from 'src/app/shared/services/notification/notification-agreement-request';
import { NotificationExpressionOfInterestExpired } from 'src/app/shared/services/notification/notification-expression-of-interest-expired';
import { NotificationScheduling } from 'src/app/shared/services/notification/notification-scheduling';
import { NotificationSupportHour } from 'src/app/shared/services/notification/notification-support-hour';
import { NotificationTaxAndSuperSync } from 'src/app/shared/services/notification/notification-tax-and-super-sync';
import { FirebaseAnalyticsService } from '../../core/services/analytics/firebase-analytics.service';
import { AgreementsService } from '../../pages/agreements/services/agreements.service';
import { DeviceApp } from '../device-app.info';
import { ConsumersService } from '../services/consumers.service';
import { JobsService } from '../services/jobs.service';
import { TimesheetsService } from '../services/timesheets.service';
import { ChatService } from './chat.service';
import { NotificationAgreement } from './notification/notification-agreement';
import { NotificationBase } from './notification/notification-base';
import { NotificationConversation } from './notification/notification-conversation';
import { NotificationEvaluation } from './notification/notification-evaluation';
import { NotificationFeedback } from './notification/notification-feedback';
import { NotificationFlexAvailability } from './notification/notification-flex-availability';
import { NotificationFlexJob } from './notification/notification-flex-job';
import { NotificationGeneric } from './notification/notification-generic';
import { NotificationJob } from './notification/notification-job';
import { NotificationSendbird } from './notification/notification-sendbird';
import { NotificationTimesheet } from './notification/notification-timesheet';
import { SbInstanceService } from './sendbird/sb-instance.service';
import { ServicesProvider } from './services.provider';

declare let window: any;

@Injectable({
  providedIn: 'root',
})
export class NotificationProvider {
  notId: number;

  /** Class members */
  registrationId: string;

  private subscription: Subscription = new Subscription();

  private conversationExistSubscription: Subscription;

  private sendbirdUserSubscription: Subscription;

  private notificationClasses: any = {
    agreement: NotificationAgreement,
    agreement_requested: NotificationAgreementRequest,
    conversation: NotificationConversation,
    sendbird: NotificationSendbird,
    job: NotificationJob,
    timesheet: NotificationTimesheet,
    generic: NotificationGeneric,
    flexAvailability: NotificationFlexAvailability,
    flex_job: NotificationFlexJob,
    worker_feedback: NotificationFeedback,
    carer_reliability_assessment: NotificationEvaluation,
    scheduled_event_invited: NotificationScheduling,
    scheduled_event_updated: NotificationScheduling,
    scheduled_event_cancel: NotificationScheduling,
    scheduled_event_declined: NotificationScheduling,
    remind_session_check_in: NotificationScheduling,
    remind_session_check_out: NotificationScheduling,
    expression_of_interest_declined: NotificationExpressionOfInterestExpired,
    expression_of_interest_expired: NotificationExpressionOfInterestExpired,
    tax_and_super_sync: NotificationTaxAndSuperSync,
    support_hour_created: NotificationSupportHour,
  };

  constructor(
    private servicesProvider: ServicesProvider,
    private deviceApp: DeviceApp,
    protected firebase: FirebaseX,
    protected router: Router,
    protected navCtrl: NavController,
    protected alertCtrl: AlertController,
    protected jobsService: JobsService,
    protected timesheetsService: TimesheetsService,
    protected agreementsService: AgreementsService,
    protected consumersService: ConsumersService,
    protected modalController: ModalController,
    protected platform: Platform,
    private authService: AuthService,
    private accountService: AccountService,
    protected firebaseAnalyticsService: FirebaseAnalyticsService,
    private chatService: ChatService,
    private sbInstanceService: SbInstanceService,
    private analyticsService: AnalyticsService,
  ) {}

  /**
   * Subscribe to userAuthStatusSubject and
   * If user is logged in, register for push otherwise unregister
   */
  init() {
    this.authService.userAuthStatusSubject.pipe(distinctUntilChanged()).subscribe((isLoggedIn) => {
      // Don't register/unregister push notifications for admins
      if (this.platform.is('cordova') && !this.accountService.userIs('Admin', true)) {
        const shouldRegisterPush = isLoggedIn && this.accountService.userIs('consumer');

        if (shouldRegisterPush) {
          this.registerPush();
        } else {
          this.unregisterPush();
        }
      }
    });
  }

  // Registers this device to receive push notifications
  // https://github.com/phonegap/phonegap-plugin-push/blob/master/docs/API.md
  async registerPush(): Promise<void> {
    if (this.subscription.closed) {
      this.subscription = new Subscription();
    }

    if (this.platform.is('cordova')) {
      if (this.platform.is('ios')) {
        // This will be called once when remote notifications permission has been granted by the user at runtime.
        this.firebase
          .onApnsTokenReceived()
          .pipe(
            catchError((error) => {
              console.error(error);
              return of(null);
            }),
            filter((token) => !!token),
            take(1),
          )
          .subscribe((token) => {
            // do not track segment to prevent duplication of identify in ios
            this.registerPushNotification();
          });

        // This is only needed for iOS - need to have a permission before the apn token is generated
        await this.requestPermission();
        this.trackPushNotificationPermissionGranted();

        // do we need to implement onTokenRefresh?
      } else if (this.platform.is('android')) {
        await this.registerPushNotification();
        this.trackPushNotificationPermissionGranted();
      }
    }
  }

  // Unregister is used when app no longer wants to receive push notifications
  async unregisterPush(): Promise<void> {
    console.log('We are unregistering from push!');
    this.registrationId = null;

    // The unregistering for sendbird message push notification is done via SbInstanceService.disconnectUser().
    // This is to ensure that the unregistering is done before the user is disconnected from sendbird.
    try {
      await this.firebase.unregister();
    } catch (error) {
      console.error('Failed to unregister Firebase push', error);
    } finally {
      // Regardless of the unregister result, the app should unregister and unsubscribe
      // to Push notification handlers
      this.unsubscribeFromPush();
    }
  }

  /**
   * Processes sendbird message notifications.
   *
   * @param sendbirdPayload - the notification payload passed by sendbird
   */
  processSendbirdNotification(sendbirdPayload: any): void {
    const currentUser = this.accountService.getUser();
    const sendbirdUser = this.sbInstanceService.currentUser;

    // Guard clause to prevent notifications being displayed if user is logged out or not equal to the sendbird user
    if (!currentUser || !sendbirdUser || currentUser.custom?.user_uid !== sendbirdUser.userId) {
      console.error('User mismatch when processing sendbird notification');
      return;
    }

    // Set notification type for sendbird notifications as sendbird passes the payload under the 'sendbird' property.
    const notificationData = {
      // Adding kind and item properties to make it compatible with notification base class.
      payload: {
        kind: 'sendbird',
        item: sendbirdPayload,
      },
      // This mapping is needed because notification base class uses message and title
      title: sendbirdPayload?.push_title || 'New message',
      message: sendbirdPayload.message,
    };

    this.newNotification(notificationData).process();
  }

  processNotification(data: any): void {
    // prevent double processing bug on android when app is in
    // background/asleep
    if (this.notId === data.notId) {
      return;
    }

    this.notId = data.notId;
    data.payload = JSON.parse(data.payload);

    // For Android, body and title will be available in pushData, however for iOS it is inside the aps.alert
    if (this.platform.is('ios')) {
      data.body = data.aps.alert.body;
      data.title = data.aps.alert.title;
    }
    // This is needed because notification base class uses message instead of new body from Firebase
    data.message = data.body;

    if (this.notificationClasses[data.payload.kind]) {
      this.newNotification(data).process();
    }
  }

  // Instantiating a class using a string. String is mapped to a class.
  newNotification(data: any): NotificationBase {
    const { kind } = data.payload;

    return new this.notificationClasses[kind](
      this.router,
      this.navCtrl,
      this.alertCtrl,
      this.agreementsService,
      this.jobsService,
      this.consumersService,
      this.timesheetsService,
      data,
      this.modalController,
      this.authService,
      this.accountService,
      this.firebaseAnalyticsService,
    );
  }

  /**
   * Registers a callback function to invoke when:
   * - a notification or data message is received by the app
   * - a system notification is tapped by the user
   *
   *
   * Sendbird by default sends notifications of type 'data'. FirebaseX doesn't process notifications of type data in killed mode.
   * To support all three modes, sendbird has enabled a custom feature on our apps that adds the 'notification' property to push.
   * Its not a global setting and needs to be enabled for all applications separately (staging/uat/prod)
   */
  private subscribeToPush(): void {
    // Custom FCM receiver plugin - Registering a callback function for sendbird background/foreground notifications.
    window?.cordova?.plugin?.customfcmreceiver?.onMessageReceived(
      (data) => {
        this.processSendbirdNotification(data?.sendbird);
      },
      (error) => {
        console.error('Failed to process sendbird notification: FCM', error);
      },
    );

    // Subscribe to push notifications
    this.subscription.add(
      this.firebase.onMessageReceived().subscribe((data) => {
        if ('sendbird' in data) {
          this.processSendbirdNotification(data.sendbird);
        } else {
          this.processNotification(data);
        }
      }),
    );
  }

  /**
   * Listen to sendbird connection
   */
  watchForSendbirdConnection() {
    this.sendbirdUserSubscription = this.chatService.getSendBirdUser().subscribe((sendBirdUser) => {
      console.log(`user connected - ${JSON.stringify(sendBirdUser)}`);
      if (sendBirdUser) {
        this.registerSendBirdNotifications();
      } else {
        // remove the unregister event handler callback once the user has been disconnected
        this.sbInstanceService.addUnregisterPushNotificationFn = null;
      }
    });
  }

  /**
   * Listen to conversation existance change
   */
  watchForConversationExists() {
    this.conversationExistSubscription = this.chatService
      .getConversationExists()
      .pipe(
        filter((item) => !!item), // Skip initial null value
        distinctUntilChanged(),
      )
      .subscribe(async (conversationExist) => {
        console.log(`conversation exist - ${conversationExist}`);
        if (conversationExist) {
          this.watchForSendbirdConnection();
        } else {
          this.unregisterSendBirdNotifications();
        }
      });
  }

  async registerSendBirdNotifications() {
    const sb = this.sbInstanceService.instance;

    // add the unregister callback function. This is called before disconnecting the user from sendbird.
    // TODO (ES-25768) - We can move it to constructor but leaving it for refactor.
    this.sbInstanceService.addUnregisterPushNotificationFn = () => this.unregisterSendBirdNotifications();

    if (this.platform.is('cordova')) {
      if (this.platform.is('ios')) {
        const token = await this.firebase.getAPNSToken();
        try {
          const response = await sb.registerAPNSPushTokenForCurrentUser(token);
          console.log(`register ios - ${response}`);
          // Do something in response to a successful registration.
        } catch (error) {
          console.error('failed to register sendbird apn token for ios', error);
          // Handle error.
        }
      } else if (this.platform.is('android')) {
        const token = await this.firebase.getToken();
        try {
          const response = await sb.registerFCMPushTokenForCurrentUser(token);
          console.log(`register android - ${response}`);
          // Do something in response to a successful registration.
        } catch (error) {
          console.error('failed to register sendbird apn token for android', error);
          // Handle error.
        }
      }
    }
  }

  /**
   * Unregister the user from the Sendbird push notification service.
   */
  async unregisterSendBirdNotifications(): Promise<void> {
    const sb = this.sbInstanceService.instance;

    if (this.platform.is('ios')) {
      try {
        const token = await this.firebase.getAPNSToken();
        const response = await sb.unregisterAPNSPushTokenForCurrentUser(token);
        console.log(`unregister ios - ${response}`);
        // Do something in response to a successful unregistration.
      } catch (error) {
        console.error('failed to unregister ios from sendbird', error);
      }
    } else if (this.platform.is('android')) {
      try {
        const token = await this.firebase.getToken();
        const response = await sb.unregisterFCMPushTokenForCurrentUser(token);
        console.log(`unregister android - ${response}`);
        // Do something in response to a successful unregistration.
      } catch (error) {
        console.error('failed to unregister android from sendbird', error);
      }
    }
  }

  private unsubscribeFromPush(): void {
    this.subscription?.unsubscribe();
    this.conversationExistSubscription?.unsubscribe();
    this.sendbirdUserSubscription?.unsubscribe();

    // Resetting custom FCM callback.
    const customFCMPluginAPI = window?.cordova?.plugin?.customfcmreceiver;
    if (typeof customFCMPluginAPI?.onMessageReceived === 'function') {
      customFCMPluginAPI.onMessageReceived(() => {});
    }
  }

  private async requestPermission(): Promise<boolean> {
    let hasPermission = await this.firebase.hasPermission();
    if (!hasPermission) {
      hasPermission = await this.firebase.grantPermission();
    }

    return hasPermission;
  }

  private async trackPushNotificationPermissionGranted(): Promise<void> {
    const hasPermission = await this.firebase.hasPermission();
    this.analyticsService.identify({
      push_notifications_enabled: hasPermission,
    });
  }

  private async registerPushNotification() {
    this.subscribeToPush();

    const token = await this.firebase.getToken();
    if (this.registrationId !== token) {
      this.registrationId = token;

      console.log(`registerPush token: ${token}`);
      await this.requestPermission();

      this.deviceApp.getInfo().then((info) => {
        this.servicesProvider.registerDevice(token, info);
      });

      // Register/Unregister SendBird push notifications according to channel existance.
      await this.watchForConversationExists();
    }
  }
}
